import { faMinus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Calendar } from 'primereact/calendar';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dispatch, useMemo } from 'react';
import { useState } from 'react';
import { SetStateAction } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../hooks/useAxiosHook';
import useHaveValuesChanged from '../../../hooks/useHaveValuesChanged';
import useSearchQueryDateParam from '../../../hooks/useSearchQueryDateParam';
import useSearchQueryDropdownParam from '../../../hooks/useSearchQueryDropdownParam';
import useSearchQueryParam from '../../../hooks/useSearchQueryParam';
import { WithPaginationAndSummary } from '../../../types/api';
import { SmartPOSTerminals } from '../../../types/api/smartPosTerminals';
import { debounceTimeout } from '../../../utils/constants/misc';
import { httpDateFormat } from '../../../utils/helpers/formatting';
import { renderIcon } from '../../../utils/helpers/icon';
import {
  getSearchQueryParam,
  tryDateSearchParam,
} from '../../../utils/helpers/searchQuery';
import FiltersCounter from '../Components/Filters/FiltersCounter';
import {
  paymentsStatuses,
  paymentsTerminalOptions,
} from './Payments.functions';

function useTableFilters(
  page: number,
  setPage: Dispatch<SetStateAction<number>>,
  limit: number
) {
  const { t } = useTranslation();
  const location = useLocation();
  const [dateFrom, setDateFrom] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'dateFrom') ?? ''
      ) ?? dayjs().toDate()
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'dateTo') ?? ''
      ) ?? dayjs().toDate()
  );
  const [amountFrom, setAmountFrom] = useState<number | any>(
    () => getSearchQueryParam(location.search, 'amountFrom') ?? null
  );
  const [amountTo, setAmountTo] = useState<number | any>(
    () => getSearchQueryParam(location.search, 'amountTo') ?? null
  );
  const [customerName, setCustomerName] = useState<string>(
    () => getSearchQueryParam(location.search, 'customerName') ?? ''
  );
  const [serialNumber, setSerialNumber] = useState<string>(
    () => getSearchQueryParam(location.search, 'serialNumber') ?? ''
  );
  const [paymentTypeId, setPaymentTypeId] = useState<string>(
    () => getSearchQueryParam(location.search, 'paymentTypeId') ?? ''
  );
  const [paymentStatusId, setPaymentStatusId] = useState<string>(
    () => getSearchQueryParam(location.search, 'paymentStatusId') ?? ''
  );
  const [posTerminalId, setPosTerminalId] = useState<string>(
    () => getSearchQueryParam(location.search, 'posTerminalId') ?? ''
  );

  const statusesOptions = useMemo(() => paymentsStatuses(t), [t]);
  const terminalOptions = useMemo(() => paymentsTerminalOptions(t), [t]);

  const debouncedCustomerName = useDebounce(customerName, debounceTimeout);
  const debouncedSerialNumber = useDebounce(serialNumber, debounceTimeout);
  const debouncedAmountFrom = useDebounce(amountFrom, debounceTimeout);
  const debouncedAmountTo = useDebounce(amountTo, debounceTimeout);

  const { data: posTerminals } = useAxiosHook<any>('/smart-pos-terminals');

  const posTerminalOptions = useMemo<{ label: string; value: string }[]>(
    () =>
      posTerminals?.data?.map((p: any) => ({
        label: p.terminalName,
        value: p.id,
      })) ?? [],
    [posTerminals?.data]
  );

  const filtersArr = useMemo(
    () => [
      dateFrom,
      dateTo,
      paymentTypeId,
      paymentStatusId,
      posTerminalId,
      debouncedCustomerName,
      debouncedSerialNumber,
      debouncedAmountFrom,
      debouncedAmountTo,
    ],
    [
      dateFrom,
      dateTo,
      paymentTypeId,
      paymentStatusId,
      posTerminalId,
      debouncedCustomerName,
      debouncedSerialNumber,
      debouncedAmountFrom,
      debouncedAmountTo,
    ]
  );

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  useEffect(() => {
    if (!setPage) {
      return;
    }
    setPage(1);
  }, [
    setPage,
    dateFrom,
    dateTo,
    paymentTypeId,
    paymentStatusId,
    posTerminalId,
    haveFiltersChanged,
  ]);

  const httpFiltersObj = useMemo(() => {
    const _dateFrom = dayjs(dateFrom as any);
    const _dateTo = dayjs(dateTo as any);
    return {
      page: haveFiltersChanged ? 1 : page,
      limit,
      dateFrom: httpDateFormat(_dateFrom),
      dateTo: httpDateFormat(_dateTo),
      amountFrom: debouncedAmountFrom,
      amountTo: debouncedAmountTo,
      customerName: debouncedCustomerName,
      serialNumber: debouncedSerialNumber,
      paymentTypeId: paymentTypeId,
      paymentStatusId: paymentStatusId,
      posTerminalId: posTerminalId,
    };
  }, [
    haveFiltersChanged,
    dateFrom,
    dateTo,
    debouncedCustomerName,
    debouncedSerialNumber,
    debouncedAmountFrom,
    debouncedAmountTo,
    paymentTypeId,
    paymentStatusId,
    posTerminalId,
    limit,
    page,
  ]);

  useSearchQueryDateParam('dateFrom', dateFrom);
  useSearchQueryDateParam('dateTo', dateTo);
  useSearchQueryParam('customerName', debouncedCustomerName);
  useSearchQueryParam('serialNumber', debouncedSerialNumber);
  useSearchQueryDropdownParam(
    'paymentTypeId',
    paymentTypeId,
    setPaymentTypeId,
    terminalOptions
  );
  useSearchQueryDropdownParam(
    'paymentStatusId',
    paymentStatusId,
    setPaymentStatusId,
    statusesOptions
  );
  useSearchQueryDropdownParam(
    'posTerminalId',
    posTerminalId,
    setPosTerminalId,
    posTerminalOptions
  );

  const basicFiltersActiveFilterCount = Object.values({
    dateFrom,
    amountFrom,
    customerName,
    serialNumber,
    paymentTypeId,
    paymentStatusId,
    posTerminalId,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_date_from">{t('Created Date')}</label>
            <div className="sidebar_filter_row">
              <Calendar
                id="filter_date_from"
                value={dateFrom ?? undefined}
                monthNavigator
                yearNavigator
                dateFormat="dd/mm/yy"
                yearRange={`2009:${dayjs().format('YYYY')}`}
                maxDate={new Date()}
                onChange={(e) => setDateFrom(e.value as Date | null)}
                inputClassName={classNames({
                  sidebar_filter_active: dateFrom,
                })}
              />
              <span style={{ margin: 'auto' }}>{renderIcon(faMinus)}</span>
              <Calendar
                id="filter_date_to"
                value={dateTo ?? undefined}
                onChange={(e) => {
                  setDateTo(e.value as Date | null);
                }}
                dateFormat="dd/mm/yy"
                maxDate={new Date()}
                monthNavigator
                yearNavigator
                yearRange={`2009:${dayjs().format('YYYY')}`}
                inputClassName={classNames({
                  sidebar_filter_active: dateTo,
                })}
              />
            </div>
          </div>
          <div className="sidebar_filter">
            <label htmlFor="customer_name_filter">{t('Customer Name')}</label>
            <InputText
              id="customer_name_filter"
              onChange={(e) => setCustomerName(e.target.value)}
              value={customerName ?? ''}
              className={classNames({
                sidebar_filter_active: customerName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="serial_number_filter">{t('Serial Number')}</label>
            <InputText
              id="serial_number_filter"
              onChange={(e) => setSerialNumber(e.target.value)}
              value={serialNumber ?? ''}
              className={classNames({
                sidebar_filter_active: serialNumber,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="filter_amount_from">{t('Amount')}</label>
            <div className="sidebar_filter_row">
              <InputNumber
                id="amount_from"
                min={0}
                max={100}
                minFractionDigits={1}
                maxFractionDigits={3}
                step={0.001}
                value={amountFrom ?? ''}
                onChange={(e) => setAmountFrom(e.value)}
                className={classNames(
                  {
                    sidebar_filter_active: amountFrom,
                  },
                  'number-group'
                )}
              />
              <span style={{ margin: 'auto' }}>{renderIcon(faMinus)}</span>
              <InputNumber
                id="amount_to"
                min={0}
                max={100}
                minFractionDigits={1}
                maxFractionDigits={3}
                step={0.001}
                value={amountTo ?? ''}
                onChange={(e) => setAmountTo(e.value)}
                className={classNames(
                  {
                    sidebar_filter_active: amountTo,
                  },
                  'number-group'
                )}
              />
            </div>
          </div>
          <div className="sidebar_filter">
            <label htmlFor="status_id_filter">{t('Status')}</label>
            <Dropdown
              id="status_id_filter"
              options={statusesOptions}
              value={paymentStatusId}
              onChange={(e: DropdownChangeParams) =>
                setPaymentStatusId(e.value)
              }
              showClear
              className={classNames({
                sidebar_filter_active: paymentStatusId,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="status_id_filter">{t('Type Payments')}</label>
            <Dropdown
              id="status_id_filter"
              options={terminalOptions}
              value={paymentTypeId}
              onChange={(e: DropdownChangeParams) => setPaymentTypeId(e.value)}
              showClear
              className={classNames({
                sidebar_filter_active: paymentTypeId,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="status_id_filter">{t('Smart POS Terminals')}</label>
            <Dropdown
              id="status_id_filter"
              options={posTerminalOptions}
              value={posTerminalId}
              onChange={(e: DropdownChangeParams) => setPosTerminalId(e.value)}
              showClear
              className={classNames({
                sidebar_filter_active: posTerminalId,
              })}
            />
          </div>
        </AccordionTab>
      </Accordion>
    ),
    [
      basicFiltersActiveFilterCount,
      dateFrom,
      dateTo,
      amountFrom,
      amountTo,
      customerName,
      serialNumber,
      paymentStatusId,
      posTerminalId,
      paymentTypeId,
      statusesOptions,
      terminalOptions,
      posTerminalOptions,
      t,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setDateFrom(new Date());
    setDateTo(new Date());
    setAmountFrom(null);
    setAmountTo(null);
    setCustomerName('');
    setSerialNumber('');
    setPaymentStatusId('');
    setPaymentTypeId('');
    setPosTerminalId('');
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
